import flattenHierarchy from "backend/flattenHierarchy";
import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { changeColorBrightness } from "qpr-base/utils/color";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import { StrategyCard } from "../cards/StrategyCard";
import StrategyMapRowName from "../texts/StrategyMapRowName";
import ThemeTitle from "../texts/ThemeTitle";
const useStrategyTableWithThemesStyles = makeThemedStyles(
  "StrategyTableWithThemes",
  (theme) => ({
    root: {
      display: "flex",
      width: "100%",
      flexDirection: "column",
      alignItems: "start",
      gap: "20px",
    },
    row: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      gap: "20px",
    },
    leftTitle: {
      width: "150px",
    },
    cell: {
      width: "250px",
    },
  })
);

const StrategyGridWithThemes = (props: {
  elements: ICardElementWithChildren[];
  elementBgImages?: string[];
  primaryThemeColors?: string[];
  primaryThemeTextColors?: string[];
  defaultThemeColor?: string;
  themes: ICardElementWithChildren[];
  onSelect?: (el: ICardElementWithChildren) => void;
}) => {
  const {
    elements: data,
    themes,
    primaryThemeColors,
    primaryThemeTextColors,
  } = props;
  const styles = useStrategyTableWithThemesStyles();
  const ctx = useAppContext();
  const themesOfElements = React.useMemo(() => {
    const res: Record<string, number> = {};
    const list = flattenHierarchy(data);

    if (!list) return res;
    if (!themes) return res;

    list.forEach((el) => {
      const hasTheme = themes.find((theme) => el.symbol.includes(theme.symbol));
      if (hasTheme && hasTheme.symbol !== "T4") {
        res[el.id] = themes.indexOf(hasTheme);
      }
    });
    return res;
  }, [data, themes]);

  return (
    <>
      <div className={styles.root} key={`StrategyTable${data?.[0]?.id}`}>
        <div className={styles.row} key="Themes">
          <div className={styles.leftTitle} />
          {themes
            ? themes.map((t, i) => (
                <div className={styles.cell} key={t.id}>
                  <ThemeTitle
                    textColor={
                      primaryThemeTextColors
                        ? changeColorBrightness(primaryThemeTextColors[i], 0.95)
                        : undefined
                    }
                    background={
                      primaryThemeColors
                        ? changeColorBrightness(primaryThemeColors[i], 0.95)
                        : undefined
                    }
                  >
                    {getTranslatedAttribute(t, "Name", ctx.language)}
                  </ThemeTitle>
                </div>
              ))
            : null}
        </div>
        {data?.map((el, idx: number) => (
          <div className={styles.row} key={el.id}>
            <div className={styles.leftTitle} key={`Heading ${el.id}`}>
              <StrategyMapRowName>
                {getTranslatedAttribute(el, "Name", ctx.language)}
              </StrategyMapRowName>
            </div>
            {el.children
              .sort((a, b) => a.symbol.localeCompare(b.symbol))
              .map((ch, index, arr) => {
                const sameLevelChildCount = arr.filter(
                  (child) =>
                    themesOfElements[ch.id] &&
                    themesOfElements[ch.id] === themesOfElements[child.id]
                ).length;
                return (
                  <div
                    className={styles.cell}
                    key={ch.id}
                    style={{
                      order: themesOfElements[ch.id],
                      width: sameLevelChildCount > 1 ? "115px" : undefined,
                    }}
                  >
                    <StrategyCard
                      primaryColor={
                        typeof themesOfElements?.[ch.id] !== "undefined" &&
                        primaryThemeColors
                          ? primaryThemeColors[themesOfElements[ch.id]]
                          : props.defaultThemeColor
                      }
                      textColor={
                        typeof themesOfElements?.[ch.id] !== "undefined" &&
                        primaryThemeTextColors
                          ? primaryThemeTextColors[themesOfElements[ch.id]]
                          : undefined
                      }
                      narrow={sameLevelChildCount > 1}
                      typenames={[
                        {
                          name: "KPI",
                          title: t("kpis.KPIs"),
                        },
                        {
                          name: "Initiative",
                          title: t("initiatives.Initiatives"),
                        },
                      ]}
                      element={ch}
                      translatedName={getTranslatedAttribute(
                        ch,
                        "Name",
                        ctx.language
                      )}
                      onSelect={() => {
                        if (props.onSelect) {
                          props.onSelect(ch);
                        }
                      }}
                    />
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </>
  );
};

export default StrategyGridWithThemes;
